import { Tooltip } from 'antd'
import { TooltipPlacement } from 'antd/es/tooltip'
import {
  EXPLORER_BORDER_GRAY_LOW_OPACITY,
  EXPLORER_INPUT_GRAY,
} from 'features/drilldown/colors'
import { useOntology } from 'features/dynamicOntology'
import isNil from 'lodash/isNil'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'

import { formatValueForDisplay } from './utils'

const PopupContainer = styled.div<{ maxWidth?: string }>`
  width: ${(props) => props.maxWidth || '550px'};
  height: 450px;
  overflow-y: auto;
  padding: 1rem;
  background: ${EXPLORER_INPUT_GRAY};
  border: 0.5px solid ${EXPLORER_BORDER_GRAY_LOW_OPACITY};
`

const CardContainer = styled.div`
  width: '100%';
  max-width: 'none'};
  overflow-y: auto;
  padding: 0.5rem;
`

const PropertyRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-bottom: 4px;
  font-size: 12px;
  align-items: baseline;
`

const PropertyKey = styled.span`
  font-weight: 600;
  color: ${CantileverTheme.colors.gray['400']};
  white-space: nowrap;
`

const PropertyValue = styled.span`
  color: ${CantileverTheme.colors.gray['400']};
  word-break: break-word;
  flex: 1;
  min-width: 0;
`

const EmptyState = styled.div`
  color: ${CantileverTheme.colors.gray['400']};
  font-style: italic;
`

const HoverableContent = styled.span`
  cursor: help;
`

const Divider = styled.div`
  height: 1px;
  background-color: ${CantileverTheme.colors.gray['300']};
  margin: 0.5rem 0;
`

interface EntryDataPopupProps {
  entryData: Record<string, unknown>
  children?: ReactNode
  placement?: TooltipPlacement
}

/**
 * Renders a popup component that displays all properties of an entry in a formatted way.
 * Can be used as a standalone component or as a tooltip wrapper.
 *
 * As a follow-up feature, we could apply the property value renderers here by passing in the correct type.

 */
const renderPropertyValue = (value: unknown): string => {
  if (isNil(value)) {
    return '—'
  }

  // Use shared formatting logic for consistent display
  return formatValueForDisplay(value, 'text')
}

export const EntryPropertiesView: FC<{
  entryData: Record<string, unknown>
  maxWidth?: string
  isCard?: boolean
}> = ({ entryData, maxWidth, isCard = false }) => {
  const { fields } = useOntology()

  const properties = Object.entries(entryData).sort(([a], [b]) =>
    a.localeCompare(b)
  )

  // Separate known ontology properties from other properties
  const knownProperties: [string, unknown][] = []
  const otherProperties: [string, unknown][] = []

  properties.forEach(([key, value]) => {
    if (fields[key]) {
      knownProperties.push([key, value])
    } else {
      otherProperties.push([key, value])
    }
  })

  const Container = isCard ? CardContainer : PopupContainer

  return (
    <Container maxWidth={maxWidth}>
      {properties.length === 0 ? (
        <EmptyState>No properties available</EmptyState>
      ) : (
        <>
          {/* Known ontology properties */}
          {knownProperties.map(([key, value]) => (
            <PropertyRow key={key}>
              <PropertyKey>{key}:</PropertyKey>
              <PropertyValue>{renderPropertyValue(value)}</PropertyValue>
            </PropertyRow>
          ))}

          {/* Divider if we have both known and other properties */}
          {knownProperties.length > 0 && otherProperties.length > 0 && (
            <Divider />
          )}

          {/* Other properties */}
          {otherProperties.map(([key, value]) => (
            <PropertyRow key={key}>
              <PropertyKey>{key}:</PropertyKey>
              <PropertyValue>{renderPropertyValue(value)}</PropertyValue>
            </PropertyRow>
          ))}
        </>
      )}
    </Container>
  )
}

export const EntryPropertiesPopup: FC<EntryDataPopupProps> = ({
  entryData,
  children,
  placement = 'right',
}) => {
  const entryPropertiesView = <EntryPropertiesView entryData={entryData} />

  return (
    <Tooltip
      title={entryPropertiesView}
      placement={placement}
      styles={{ root: { maxWidth: 'none' } }}>
      <HoverableContent>{children}</HoverableContent>
    </Tooltip>
  )
}
